import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  api, fetchInvoiceRequestFileUrl, invoiceRequestPulse, uploadInvoiceRequestFile,
  type AcctCorp, type InvReqFile, type InvReqLookup, type InvReqMsg, type InvReqPulse, type InvReqRow, type InvReqStatus, type MisuCancelPulse,
} from '../lib/api';
import { InvoiceTab, type InvoiceExtraCols } from './Invoice';
import { money, MoneyInput } from '../components/DataTable';
import Modal from '../components/Modal';
import { useSticky } from '../lib/sticky';
import { SortTh, useSort, useSortedRows } from '../lib/sort';
import { CancelRequestDetail, CancelRequestPanel, KindChip } from './InvoiceCancelRequests';

/**
 * 세금계산서 발행 요청 — 영업 → 회계(2026-09-28). 서버 `functions/src/invoiceRequest.ts` · 기획 `plan/accounting.md` 5-J.
 *
 * 구글챗 「세금계산서 (회사매출)」 스페이스를 대체한다. 챗은 글이 흘러가 **빠진 건·처리 중인 건이 안 보였다.**
 * 여기선 요청이 상태를 갖고, 받는 쪽 화면에 **즉시 환기**한다:
 *   - 회계: 새 요청이 오면 어느 화면에 있든 팝업 + 소리 + 데스크톱 알림 · 메뉴 배지 · 탭 제목 숫자 · 30분 넘게 아무도 안 열면 빨간 띠
 *   - 영업: 보완 요청·발행 완료·답글이 오면 팝업(「확인」 누를 때까지 다시 뜬다) + 데스크톱 알림
 * 알림은 `InvoiceRequestAlerts` 하나가 45초마다(+창에 돌아올 때) 맥박을 부르고, 배지들은 그 값을 나눠 쓴다.
 */

export const STATUS: Record<InvReqStatus, { label: string; cls: string }> = {
  new: { label: '새 요청', cls: 'bg-red-100 text-red-700' },
  seen: { label: '확인중', cls: 'bg-amber-100 text-amber-800' },
  numbered: { label: '번호 매김', cls: 'bg-blue-100 text-blue-700' },
  fix: { label: '보완 요청', cls: 'bg-orange-100 text-orange-800' },
  done: { label: '발행 완료', cls: 'bg-emerald-100 text-emerald-700' },
  cancel: { label: '취소', cls: 'bg-canvas text-ink-faint' },
};
export function StatusChip({ s }: { s: InvReqStatus }) {
  return <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS[s].cls}`}>{STATUS[s].label}</span>;
}

const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));

/**
 * #307 사업자번호 확인 — 10자리면 국세청 검증번호(끝자리) 규칙으로 본다(가중치 1,3,7,1,3,7,1,3,5).
 * 주민번호(13자리, 개인 발행)와 빈칸은 null. 발행 요청 창은 #482 부터 이 문구로 막는다(빈칸·글자는 호출부가 따로).
 */
export function bizNoWarning(raw: string): string | null {
  const d = raw.replace(/[^0-9]/g, '');
  if (d === '' || d.length === 13) return null;
  if (d.length !== 10) return `사업자번호는 10자리입니다(지금 ${d.length}자리).`;
  const w = [1, 3, 7, 1, 3, 7, 1, 3, 5];
  let s = 0;
  for (let i = 0; i < 9; i++) s += Number(d[i]) * w[i]!;
  s += Math.floor((Number(d[8]) * 5) / 10);
  return (10 - (s % 10)) % 10 === Number(d[9]) ? null : '없는 사업자번호입니다 — 숫자를 다시 확인해 주세요.';
}
const ago = (min: number) => (min < 1 ? '방금' : min < 60 ? `${min}분 전` : min < 1440 ? `${Math.floor(min / 60)}시간 전` : `${Math.floor(min / 1440)}일 전`);

// ─── 맥박 공유(폴러는 하나, 읽는 곳은 여럿) ───────────────────────────────────────
let pulseNow: InvReqPulse | null = null;
const subs = new Set<(p: InvReqPulse | null) => void>();
function publish(p: InvReqPulse | null) {
  pulseNow = p;
  subs.forEach((f) => f(p));
}
export function useInvReqPulse(): InvReqPulse | null {
  const [p, setP] = useState(pulseNow);
  useEffect(() => {
    subs.add(setP);
    return () => void subs.delete(setP);
  }, []);
  return p;
}
/** 요청을 올리거나 처리한 뒤 부른다 — 45초를 기다리지 않고 배지·팝업을 다시 맞춘다. */
export function pokeInvoiceRequests() {
  window.dispatchEvent(new Event('invreq:poke'));
}

// ─── 첨부(사업자등록증 등 — 게시판 #59) ─────────────────────────────────────────────
const sizeText = (b: string | number) => {
  const n = Number(b);
  return !Number.isFinite(n) ? '' : n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)}MB` : `${Math.max(1, Math.round(n / 1024))}KB`;
};
/** 첨부 칩 — 누르면 새 탭(열람에도 로그인 토큰이 필요해서 `<a href>` 로는 못 건다). */
function FileChip({ f, canRemove, onRemove }: { f: InvReqFile; canRemove: boolean; onRemove: () => void }) {
  const [busy, setBusy] = useState(false);
  async function open() {
    setBusy(true);
    try {
      const url = await fetchInvoiceRequestFileUrl(f.id);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) { alert(errText(e)); } finally { setBusy(false); }
  }
  const icon = f.content_type.startsWith('image/') ? '🖼' : f.content_type === 'application/pdf' ? '📕' : '📄';
  return (
    <span className="inline-flex items-center gap-1 rounded-lg border border-line bg-white px-2 py-1 text-xs">
      <button className="max-w-[16rem] truncate text-left hover:underline" disabled={busy} onClick={open}>{icon} {f.file_name}</button>
      <span className="text-ink-faint">{sizeText(f.byte_size)}</span>
      {canRemove && <button className="text-ink-faint hover:text-red-600" title="첨부 내리기" onClick={onRemove}>×</button>}
    </span>
  );
}

/** 파일 고르기 버튼(여러 개). 고르면 `onPick` — 올리기는 부르는 쪽이 한다. */
function PickFiles({ label, disabled, onPick }: { label: string; disabled?: boolean; onPick: (files: File[]) => void }) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <>
      <button type="button" className="btn-ghost whitespace-nowrap" disabled={disabled} onClick={() => ref.current?.click()}>{label}</button>
      <input ref={ref} type="file" multiple className="hidden"
             onChange={(e) => { const fs = Array.from(e.target.files ?? []); e.target.value = ''; if (fs.length) onPick(fs); }} />
    </>
  );
}

/** 여러 파일을 차례로 올린다. 실패한 파일 이름을 모아 돌려준다(올라간 것은 그대로 둔다). */
async function uploadAll(id: number, files: File[]): Promise<string[]> {
  const failed: string[] = [];
  for (const f of files) {
    try { await uploadInvoiceRequestFile(id, f); } catch (e) { failed.push(`${f.name} — ${errText(e)}`); }
  }
  return failed;
}

// ─── 요청 창(영업) ──────────────────────────────────────────────────────────────
type Meta = Awaited<ReturnType<typeof api.invoiceRequestMeta>>;
let metaCache: Promise<Meta> | null = null;
/**
 * 10-30 이현숙 — 「부가세 포함」으로 넣을 금액을 ÷1.1 해서 「부가세 별도」로 넣으면 세액 절사 때문에 합계가 279,999원처럼 끝난다.
 * 공급가 × 1.1 이 100원 단위에서 1~2원 모자라고, 그 100원 단위 합계를 포함으로 넣었을 때 공급가가 ±2원 안이면 그 합계를 돌려준다.
 * (prod 최근 60일 별도 요청 중 합계 끝 95~99원 6건 = 전부 이 모양 — 254,545·309,090·981,818·363,636·154,545·1,290,909)
 */
export function roundTotalOf(supply: number): number | null {
  if (!Number.isInteger(supply) || supply <= 0) return null;
  const total = supply + Math.floor(supply / 10);
  const t = Math.ceil(total / 100) * 100;
  const gap = t - total;
  if (gap < 1 || gap > 2) return null;
  return Math.abs(Math.floor((t * 20 + 11) / 22) - supply) <= 2 ? t : null;
}

function useMeta(): Meta | null {
  const [m, setM] = useState<Meta | null>(null);
  useEffect(() => {
    metaCache ??= api.invoiceRequestMeta({});
    metaCache.then(setM).catch(() => { metaCache = null; });
  }, []);
  return m;
}

/** #234 발행방식 — 서버 `invoiceRequest.ts ISSUE_WAYS` 와 같은 목록. */
const ISSUE_WAYS = ['정발행', '역발행', '나라장터', '홈택스', '유세스 정발행', '유세스 역발행', '카드', '현금', '기타'];
/** #377 계산서가 없는 발행방식 — 이메일 필수에서 뺀다(서버 `NO_INVOICE_WAYS` 와 같은 목록). */
const NO_INVOICE_WAYS = ['카드', '현금'];

/** prefill = 거래처별 정산 줄의 「발행 요청」(10-14) — 그 접수·정산월로 열고 바로 「접수로 채우기」. 저장하면 서버가 그 줄을 청구대상(+청구요청일)으로 건다. */
export function InvoiceRequestForm({ initial, prefill, onClose, onSaved }: {
  initial?: InvReqRow; prefill?: { recvId: number; driveMon: string }; onClose: () => void; onSaved: (id: number) => void;
}) {
  const meta = useMeta();
  const [recvId, setRecvId] = useState(initial?.recv_id ? String(initial.recv_id) : prefill ? String(prefill.recvId) : '');
  const [driveMon, setDriveMon] = useState(initial?.drive_mon ?? prefill?.driveMon ?? '');
  const [corpId, setCorpId] = useState(initial ? String(initial.corp_id) : '');
  const [recvKind, setRecvKind] = useState(initial?.recv_kind ?? '');
  const [custName, setCustName] = useState(initial?.cust_name ?? '');
  const [bizNumber, setBizNumber] = useState(initial?.biz_number ?? '');
  const [email, setEmail] = useState(initial?.email ?? '');
  const [writtenDate, setWrittenDate] = useState(initial?.written_date ?? '');
  const [vatIncluded, setVatIncluded] = useState(initial?.vat_included ?? false);
  const [amount, setAmount] = useState(initial ? String(Math.round(Number(initial.vat_included ? initial.total : initial.supply))) : '');
  // 합계 끝자리 맞춤(roundTotalOf) — 맞췄으면 원래 공급가·합계를 남겨 「그대로」로 되돌린다 · keep = 그대로 둔 공급가(다시 맞추지 않음).
  const [snapped, setSnapped] = useState<{ supply: number; total: number; to: number } | null>(null);
  const [keepSupply, setKeepSupply] = useState<number | null>(null);
  function snapTotal(raw: string, included: boolean) {
    if (included) return;
    const n = Math.round(Number(raw.replace(/,/g, '')));
    const t = roundTotalOf(n);
    if (t === null || n === keepSupply) return;
    setSnapped({ supply: n, total: n + Math.floor(n / 10), to: t });
    setVatIncluded(true); setAmount(String(t));
  }
  const [item, setItem] = useState(initial?.item ?? '');
  const [note, setNote] = useState(initial?.note ?? '');
  // #134 — 계산서 비고(30번) · 메일 참조자 2. 비고1(입금 계좌)은 법인에 따라 자동이라 칸이 없다.
  const [itemNote, setItemNote] = useState(initial?.item_note ?? '');
  const [remark1, setRemark1] = useState(initial?.remark1 ?? '');
  // #234 발행방식 — 새 요청은 정발행, 고른 적 없는 옛 요청은 빈칸 그대로.
  const [issueWay, setIssueWay] = useState<string>(initial ? (initial.issue_way ?? '') : '정발행');
  const [ccName, setCcName] = useState(initial?.cc_name ?? '');
  const [ccEmail, setCcEmail] = useState(initial?.cc_email ?? '');
  const [look, setLook] = useState<InvReqLookup | null>(null);
  const [lookedKey, setLookedKey] = useState<string | null>(null);
  // 거래처에 등록된 담당자(이메일 있는 사람) — 접수번호로 불러오면 채워진다(10-08 way 「여러 개 등록해서 정산 때 선택」).
  const pickable = look?.found ? (look.contacts ?? []) : [];
  // #473 같은 접수로 앞 정산월에 발행한 계산서 이메일 — 같으면 확인 표시, 다르면 「이 이메일로 계속」.
  const prevMail = look?.found ? (look.prevMail ?? null) : null;
  const sameAsPrev = !!prevMail && email.trim().toLowerCase() === prevMail.email.trim().toLowerCase();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [needForce, setNeedForce] = useState(false);
  // 접수번호가 기본 필수(way 09-28). 없으면 사유를 적고 올린다 — 회계 목록에 「접수 없음」으로 떠서 나중에 연결한다.
  /** #474 「이미 올린 요청」의 「이 요청 고치기」 — 이 창 대신 그 요청을 고치기로 연다. */
  const [openReq, setOpenReq] = useState<number | null>(null);
  const [noRecv, setNoRecv] = useState(!!initial?.no_recv_reason);
  const [noRecvReason, setNoRecvReason] = useState(initial?.no_recv_reason ?? '');
  // 새 요청의 첨부 — 요청 번호가 생긴 뒤에 올린다(경로에 번호가 들어간다). 고치기 창에선 안 받는다(요청 창 대화에서 올린다).
  const [files, setFiles] = useState<File[]>([]);

  // #307 — 거래처·사업자번호를 치면 발행했던 계산서·기준정보 거래처에서 찾아 고른다(고르면 거래처·사업자번호·이메일이 같이 들어간다).
  const [suggFor, setSuggFor] = useState<'name' | 'no' | null>(null);
  const [sugg, setSugg] = useState<Awaited<ReturnType<typeof api.searchInvoiceCust>>['rows']>([]);
  useEffect(() => {
    const q = (suggFor === 'name' ? custName : suggFor === 'no' ? bizNumber : '').trim();
    if (!suggFor || q.length < 2) { setSugg([]); return; }
    let off = false;
    const t = setTimeout(() => {
      api.searchInvoiceCust({ q }).then((r) => { if (!off) setSugg(r.rows); }).catch(() => { if (!off) setSugg([]); });
    }, 300);
    return () => { off = true; clearTimeout(t); };
  }, [suggFor, custName, bizNumber]);
  const pickCust = (c: (typeof sugg)[number]) => {
    setCustName(c.cust_name); setBizNumber(c.biz_number);
    if (c.email && !email) setEmail(c.email);
    setSuggFor(null); setSugg([]);
  };
  // #482 계산서가 나가는 요청은 경고가 아니라 막는다(서버 `bizNoForInvoice` 와 같은 규칙) · 카드·현금은 안 본다.
  const bizWarn = NO_INVOICE_WAYS.includes(issueWay) ? null
    : !bizNumber.trim() ? '사업자번호를 넣어 주세요(개인은 주민번호 13자리).'
    : /[^0-9\s-]/.test(bizNumber) ? '사업자번호는 숫자로 적어 주세요(000-00-00000).'
    : bizNoWarning(bizNumber);

  useEffect(() => { if (meta && !writtenDate) setWrittenDate(meta.today); }, [meta, writtenDate]);
  useEffect(() => { if (!driveMon && meta) setDriveMon(meta.today.slice(0, 7)); }, [meta, driveMon]);

  // 화면 미리보기(서버와 같은 규칙: 별도 = 10% 절사 · 포함 = ÷1.1 반올림). 저장 값은 서버가 다시 계산한다.
  const calc = useMemo(() => {
    const n = Math.round(Number(amount.replace(/,/g, '')));
    if (!Number.isFinite(n) || n <= 0) return null;
    if (vatIncluded) { const s = Math.floor((n * 20 + 11) / 22); return { supply: s, vat: n - s, total: n }; }
    const v = Math.floor(n / 10); return { supply: n, vat: v, total: n + v };
  }, [amount, vatIncluded]);

  // 10-28 way — 거래처별 정산 청구액과 대조(10원 이하는 포함가 반올림이라 같다 · 서버 getInvoiceRequest settleDiff 와 같은 기준).
  //   찾아본 접수·정산월이 지금 칸과 같을 때만(번호를 바꾸면 다시 「찾기」 전까지 안 본다).
  const settleGap = useMemo(() => {
    if (!calc || !look?.found || !lookedKey || lookedKey !== `${recvId}|${driveMon}` || !/^\d{4}-\d{2}$/.test(driveMon)) return null;
    const s = look.amount ? Math.round(Number(look.amount.total)) : null;
    return s === null || Math.abs(s - calc.total) > 10 ? { settle: s } : null;
  }, [calc, look, lookedKey, recvId, driveMon]);

  async function lookup() {
    setErr(null);
    const id = Number(recvId);
    if (!Number.isInteger(id) || id <= 0) { setErr('접수번호를 숫자로 입력하세요.'); return; }
    try {
      const r = await api.lookupInvoiceRequestRecv({ recvId: id, driveMon: /^\d{4}-\d{2}$/.test(driveMon) ? driveMon : null });
      setLook(r); setLookedKey(`${recvId}|${driveMon}`);
      if (r.found) {
        if (r.corpId) setCorpId(String(r.corpId));
        if (r.recvKind) setRecvKind(r.recvKind);
        if (r.custName) setCustName(r.custName);
        if (r.bizNumber) setBizNumber(r.bizNumber);
        if (r.email) setEmail(r.email);
        if (r.amount && !amount) {
          const s = String(Math.round(Number(r.amount.supply)));
          setVatIncluded(false); setAmount(s); snapTotal(s, false);
        }
      }
    } catch (e) { setErr(errText(e)); }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (prefill && !initial) void lookup(); }, []);

  // #377 — 이메일 필수(카드·현금 청구 요청만 비워도 됨). 서버 saveInvoiceRequest 가 같은 규칙으로 막는다.
  const mailOptional = NO_INVOICE_WAYS.includes(issueWay);
  const mailMissing = !mailOptional && !email.trim();

  async function save(force = false) {
    if (!force && settleGap && !window.confirm(`입력한 합계(${money(calc!.total)}원)가 거래처별 정산 청구액(${settleGap.settle === null ? '그 달 정산 줄 없음' : `${money(settleGap.settle)}원`})과 다릅니다. 그래도 올릴까요?`)) return;
    setErr(null); setBusy(true);
    try {
      const r = await api.saveInvoiceRequest({
        id: initial?.id, corpId: Number(corpId), recvId: recvId ? Number(recvId) : null, driveMon: driveMon || null, recvKind, custName,
        bizNumber, email, writtenDate, amount, vatIncluded, item, note, force, itemNote, ccName, ccEmail, remark1, issueWay: issueWay || null,
        noRecvReason: !recvId && noRecv ? noRecvReason : undefined,
      });
      if (!initial && files.length) {
        const failed = await uploadAll(r.id, files);
        if (failed.length) alert(`요청은 올라갔지만 파일 ${failed.length}개를 올리지 못했습니다. 요청 창에서 다시 올려 주세요.\n\n${failed.join('\n')}`);
      }
      if (r.bizWarn) alert(r.bizWarn); // #482 국세청 휴업 — 저장은 됐다
      pokeInvoiceRequests();
      onSaved(r.id);
    } catch (e) {
      const m = errText(e);
      setErr(m); setNeedForce(m.includes('그래도 올리기'));
    } finally { setBusy(false); }
  }

  if (openReq) return <InvoiceRequestDetail id={openReq} startEdit onClose={onClose} onEditSaved={() => { pokeInvoiceRequests(); onSaved(openReq); }} />;

  return (
    <div className="card space-y-3 p-5 text-sm">
      <div className="flex items-center justify-between">
        <h2 className="font-bold">{initial ? `발행 요청 고치기 #${initial.id}` : '세금계산서 발행 요청'}</h2>
        <span className="text-xs text-ink-soft">올리면 회계팀 화면에 바로 알림이 뜹니다.</span>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <label className="flex flex-col"><span className="text-xs text-ink-soft">접수번호 *</span>
          <input className="field" value={recvId} inputMode="numeric" placeholder="예: 12839" onChange={(e) => setRecvId(e.target.value.replace(/\D/g, ''))} /></label>
        <label className="flex flex-col"><span className="text-xs text-ink-soft">정산월</span>
          <input type="month" className="field" value={driveMon} onChange={(e) => setDriveMon(e.target.value)} /></label>
        <div className="flex items-end"><button type="button" className="btn-ghost w-full" onClick={lookup} disabled={!recvId}>접수로 채우기</button></div>
      </div>
      {!recvId && (
        <div className="space-y-1 rounded border border-dashed px-3 py-2 text-xs">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={noRecv} onChange={(e) => setNoRecv(e.target.checked)} />
            접수번호 없이 올리기
          </label>
          {noRecv ? (
            <input className="field w-full" value={noRecvReason} maxLength={300} placeholder="사유 * (예: 접수 없는 기타 매출 · 여러 접수를 묶은 청구)"
                   onChange={(e) => setNoRecvReason(e.target.value)} />
          ) : (
            <p className="text-ink-soft">접수번호가 있어야 나중에 청구·미수와 이어집니다. 접수가 없는 청구만 체크하고 사유를 적어 주세요.</p>
          )}
        </div>
      )}
      {look && !look.found && (
        <div className="rounded bg-amber-50 px-3 py-2 text-xs text-amber-900">
          {look.outOfScope
            ? '담당 범위 밖의 접수라 불러오지 않았습니다. 아래 칸을 직접 채워 올리시면 됩니다.'
            : '버스인웹에 아직 없는 접수입니다(버스인에 방금 넣은 접수일 수 있습니다). 접수번호는 그대로 두고 아래 칸을 채워 올리시면, 접수가 들어오는 대로 이어집니다.'}
        </div>
      )}
      {look?.found && (look.invoices.length > 0 || look.requests.length > 0) && (
        <div className="rounded bg-amber-50 px-3 py-2 text-xs text-amber-900">
          {look.invoices.length > 0 && <div>이 접수·정산월에 이미 계산서가 있습니다: {look.invoices.map((i) => `${i.invoice_no}${i.issuer ? '(발행)' : ''}`).join(', ')}</div>}
          {look.requests.length > 0 && (
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span>이미 올린 요청:</span>
              {look.requests.map((q) => (
                <span key={q.id} className="inline-flex items-center gap-1">
                  #{q.id} {STATUS[q.status].label}
                  {/* #474 내 요청이 아직 끝나지 않았으면 새로 올리지 않고 그 요청을 고친다(사업자번호·이메일 등). */}
                  {q.mine && q.status !== 'done' && q.status !== 'cancel' && (
                    <button type="button" className="btn border border-amber-400 bg-white px-2 py-0.5 text-xs" onClick={() => setOpenReq(q.id)}>이 요청 고치기</button>
                  )}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <label className="flex flex-col"><span className="text-xs text-ink-soft">청구법인 *</span>
          <select className="field" value={corpId} onChange={(e) => setCorpId(e.target.value)}>
            <option value="">고르세요</option>
            {meta?.corps.map((c) => <option key={c.corp_id} value={c.corp_id}>{c.short_name}</option>)}
          </select></label>
        <label className="flex flex-col"><span className="text-xs text-ink-soft">구분</span>
          <input className="field" value={recvKind} list="invreq-kinds" onChange={(e) => setRecvKind(e.target.value)} />
          <datalist id="invreq-kinds"><option value="통근" /><option value="일반" /><option value="기타" /></datalist></label>
        <label className="col-span-2 flex flex-col"><span className="text-xs text-ink-soft">거래처 *</span>
          <input className="field" value={custName} placeholder="이름 두 글자 이상 치면 아래에서 고릅니다"
                 onChange={(e) => { setCustName(e.target.value); setSuggFor('name'); }} /></label>
        <label className="flex flex-col"><span className="text-xs text-ink-soft">사업자번호</span>
          <input className={`field ${bizWarn ? 'border-amber-500' : ''}`} value={bizNumber} placeholder="번호로도 찾습니다"
                 onChange={(e) => { setBizNumber(e.target.value); setSuggFor('no'); }} />
          {bizWarn && <span className="mt-0.5 text-xs text-amber-700">{bizWarn}</span>}</label>
        {suggFor && sugg.length > 0 && (
          <div className="col-span-2 max-h-48 overflow-y-auto sm:col-span-4 rounded-lg border border-line bg-white text-sm shadow-sm">
            <div className="flex items-center justify-between border-b px-2 py-1 text-xs text-ink-soft">
              <span>발행했던 계산서·등록된 거래처에서 고르기 ({sugg.length})</span>
              <button type="button" className="underline" onClick={() => { setSuggFor(null); setSugg([]); }}>닫기</button>
            </div>
            {sugg.map((c) => (
              <button key={`${c.cust_name}|${c.biz_number}`} type="button" className="flex w-full items-center gap-2 px-2 py-1.5 text-left hover:bg-brand-50" onClick={() => pickCust(c)}>
                <span className="font-medium">{c.cust_name}</span>
                <span className="font-mono text-xs">{c.biz_number}</span>
                <span className="ml-auto text-xs text-ink-soft">{c.issued > 0 ? `발행 ${c.issued}장 · 최근 ${c.last_date ?? ''}` : '거래처 등록'}{c.email ? ` · ${c.email}` : ''}</span>
              </button>
            ))}
          </div>
        )}
        <label className="col-span-2 flex flex-col"><span className="text-xs text-ink-soft">{mailOptional ? '이메일(카드·현금은 비워도 됨)' : '이메일 *'}</span>
          <input className={`field ${mailMissing ? 'border-amber-500' : ''}`} value={email} placeholder="세금계산서 받을 이메일"
                 onChange={(e) => setEmail(e.target.value)} />
          {pickable.length > 0 && (
            <select className="field mt-1 text-xs" value="" onChange={(e) => { const c = pickable[Number(e.target.value)]; if (c) setEmail(c.email); }}>
              <option value="">등록된 담당자에서 고르기 ({pickable.length}명)</option>
              {pickable.map((c, i) => <option key={i} value={i}>{contactLabel(c)}</option>)}
            </select>
          )}
          {prevMail && (sameAsPrev ? (
            <span className="mt-1 text-xs text-emerald-700">✓ 전월 발행 이메일과 같습니다({prevMail.drive_mon} · {prevMail.invoice_no})</span>
          ) : (
            <span className="mt-1 flex flex-wrap items-center gap-1 text-xs text-amber-800">
              <span>전월 발행 이메일: <b className="break-all">{prevMail.email}</b>({prevMail.drive_mon})</span>
              <button type="button" className="btn border border-amber-400 bg-white px-2 py-0.5 text-xs" onClick={() => setEmail(prevMail.email)}>이 이메일로 계속</button>
            </span>
          ))}</label>
        <label className="flex flex-col"><span className="text-xs text-ink-soft">발행날짜 *</span>
          <input type="date" className="field" value={writtenDate} onChange={(e) => setWrittenDate(e.target.value)} /></label>
        <label className="col-span-2 flex flex-col"><span className="text-xs text-ink-soft">금액 *</span>
          <MoneyInput value={amount}
            onChange={(v) => { setAmount(v.replace(/-/g, '')); setSnapped(null); }}
            onBlur={() => snapTotal(amount, vatIncluded)} /></label>
        <div className="col-span-2 flex items-end gap-2">
          <button type="button" className={`btn ${!vatIncluded ? 'bg-brand-600 text-white' : 'border border-line'}`}
            onClick={() => { setVatIncluded(false); setSnapped(null); snapTotal(amount, false); }}>부가세 별도</button>
          <button type="button" className={`btn ${vatIncluded ? 'bg-brand-600 text-white' : 'border border-line'}`} onClick={() => setVatIncluded(true)}>부가세 포함</button>
        </div>
      </div>
      {calc && (
        <div className="rounded bg-canvas px-3 py-2 text-xs">
          공급가액 <b>{money(calc.supply)}</b> · 세액 <b>{money(calc.vat)}</b> · 합계 <b>{money(calc.total)}</b>원
        </div>
      )}
      {snapped && vatIncluded && amount === String(snapped.to) && (
        <div className="flex flex-wrap items-center gap-2 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <span>부가세 별도 {money(snapped.supply)}원은 합계가 <b>{money(snapped.total)}</b>원이 되어, 합계 <b>{money(snapped.to)}</b>원(부가세 포함)으로 맞췄습니다.</span>
          <button type="button" className="btn border border-amber-400 bg-white px-2 py-0.5 text-xs"
            onClick={() => { setKeepSupply(snapped.supply); setVatIncluded(false); setAmount(String(snapped.supply)); setSnapped(null); }}>
            {money(snapped.total)}원 그대로
          </button>
        </div>
      )}
      {settleGap && (
        <div className="rounded border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800">
          ⚠ 거래처별 정산 청구액({settleGap.settle === null ? '그 달 정산 줄 없음' : `합계 ${money(settleGap.settle)}원`})과 다릅니다. 정산 청구액을 먼저 맞추거나, 다른 이유가 있으면 특이사항에 적어 주세요.
        </div>
      )}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="flex flex-col"><span className="text-xs text-ink-soft">품목(비우면 「차량운행료」)</span>
          <input className="field" value={item} maxLength={200} placeholder="예: 동영관광_26년05월특근" onChange={(e) => setItem(e.target.value)} /></label>
        <label className="flex flex-col"><span className="text-xs text-ink-soft">비고(계산서에 나감)</span>
          <input className="field" value={itemNote} maxLength={200} placeholder="예: PO Number - 5600145774" onChange={(e) => setItemNote(e.target.value)} /></label>
        <label className="flex flex-col"><span className="text-xs text-ink-soft">메일 참조자 2 이름</span>
          <input className="field" value={ccName} maxLength={50} onChange={(e) => setCcName(e.target.value)} /></label>
        <label className="flex flex-col"><span className="text-xs text-ink-soft">메일 참조자 2 이메일</span>
          <input className="field" type="email" value={ccEmail} maxLength={300} onChange={(e) => setCcEmail(e.target.value.trim())} />
          {pickable.length > 0 && (
            <select className="field mt-1 text-xs" value="" onChange={(e) => { const c = pickable[Number(e.target.value)]; if (c) { setCcName(c.name); setCcEmail(c.email); } }}>
              <option value="">등록된 담당자에서 고르기</option>
              {pickable.map((c, i) => <option key={i} value={i}>{contactLabel(c)}</option>)}
            </select>
          )}</label>
      </div>
      {/* #223 — 몇몇 거래처는 원하는 입금 계좌를 적는다. 비우면 지금처럼 청구법인 계좌. */}
      <label className="flex flex-col"><span className="text-xs text-ink-soft">비고1(입금 계좌) — 비우면 청구법인 계좌가 자동으로 들어갑니다</span>
        <input className="field" value={remark1} maxLength={200} placeholder="예: 국민은행 123456-01-123456 (주)동영관광" onChange={(e) => setRemark1(e.target.value)} /></label>
      {/* #234 — 거래처·담당자마다 발행방식이 달라 영업이 고르고 회계가 발행대장에서 본다. */}
      <div className="flex flex-col gap-1"><span className="text-xs text-ink-soft">발행방식(기본값: 정발행 · 계산서 없이 카드·현금으로 받는 건은 「카드」·「현금」)</span>
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {ISSUE_WAYS.map((w) => (
            <label key={w} className="flex items-center gap-1.5 text-sm">
              <input type="radio" name="issue-way" checked={issueWay === w} onChange={() => setIssueWay(w)} />{w}
            </label>
          ))}
        </div></div>
      <label className="flex flex-col"><span className="text-xs text-ink-soft">특이사항(회계팀에 남길 말)</span>
        <textarea className="field min-h-[72px]" value={note} placeholder="예: ICT 세금계산서는 종사업자번호 1번(서울)으로 발급" onChange={(e) => setNote(e.target.value)} /></label>
      {!initial && (
        <div className="flex flex-wrap items-center gap-2">
          <PickFiles label="📎 파일 첨부" disabled={busy} onPick={(fs) => setFiles((cur) => [...cur, ...fs])} />
          {files.length === 0 && <span className="text-xs text-ink-soft">신규 거래처 사업자등록증 등 — 사진·PDF·엑셀 모두 됩니다(파일당 20MB).</span>}
          {files.map((f, i) => (
            <span key={`${f.name}-${i}`} className="inline-flex items-center gap-1 rounded-lg border border-line bg-canvas px-2 py-1 text-xs">
              {f.name} <span className="text-ink-faint">{sizeText(f.size)}</span>
              <button type="button" className="text-ink-faint hover:text-red-600" title="빼기" onClick={() => setFiles((cur) => cur.filter((_, j) => j !== i))}>×</button>
            </span>
          ))}
        </div>
      )}
      {err && <div className="rounded bg-red-50 px-3 py-2 text-red-700">{err}</div>}
      <div className="flex justify-end gap-2">
        <button className="btn-ghost" onClick={onClose} disabled={busy}>취소</button>
        {needForce && <button className="btn-ghost" onClick={() => save(true)} disabled={busy}>그래도 올리기</button>}
        <button className="btn-primary" onClick={() => save(false)} disabled={busy || !corpId || !custName || !writtenDate || !calc || mailMissing || !!bizWarn}
                title={mailMissing ? '세금계산서를 받을 이메일을 넣어 주세요' : bizWarn ?? undefined}>
          {busy ? '올리는 중…' : initial ? '고쳐서 다시 올리기' : '요청 올리기'}
        </button>
      </div>
    </div>
  );
}

// ─── 한 건(영업·회계 공용) ─────────────────────────────────────────────────────
/** startEdit = 열자마자 「요청 고치기」(#474 요청 창에서 넘어올 때) · onEditSaved = 고쳐 저장한 뒤(있으면 상세로 돌아가지 않고 이것을 부른다). */
export function InvoiceRequestDetail({ id, onClose, startEdit, onEditSaved }: { id: number; onClose: () => void; startEdit?: boolean; onEditSaved?: () => void }) {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.getInvoiceRequest>> | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(false);
  const [linkNo, setLinkNo] = useState('');
  const [linkRecv, setLinkRecv] = useState('');
  const [linkMon, setLinkMon] = useState('');
  const [series, setSeries] = useState('');

  const load = useCallback(() => {
    api.getInvoiceRequest({ id }).then((d) => { setData(d); pokeInvoiceRequests(); }).catch((e) => setErr(errText(e)));
  }, [id]);
  useEffect(load, [load]);

  async function run(f: () => Promise<unknown>, clear = true) {
    setErr(null); setBusy(true);
    try { await f(); if (clear) setMsg(''); load(); pokeInvoiceRequests(); } catch (e) { setErr(errText(e)); } finally { setBusy(false); }
  }

  // 첨부는 올릴 때 남긴 「📎」 대화 줄 안에 그린다. 내린 파일은 그 줄에 「(내림)」만 남는다.
  const fileOf = new Map((data?.files ?? []).filter((f) => f.msg_id).map((f) => [f.msg_id!, f] as const));
  const canRemove = (f: InvReqFile) => !!data?.acct || (!!data?.mine && f.side === 'sales');
  const removeFile = (f: InvReqFile) => {
    if (!confirm(`「${f.file_name}」을 내릴까요?`)) return;
    void run(() => api.deleteInvoiceRequestFile({ fileId: f.id }), false);
  };

  // 번호까지 매긴 요청은 상세의 「요청 고치기」 확인창을 거치게 둔다(고치면 발행대장 번호가 내려간다).
  const autoEdit = !!startEdit && !!data?.mine && !!data.row && ['open', 'fix'].includes(data.row.state) && !data.row.invoice_issuer && !data.row.invoice_id;
  if ((edit || autoEdit) && data) {
    return <InvoiceRequestForm initial={data.row} onClose={() => (autoEdit && !edit ? onClose() : setEdit(false))}
      onSaved={() => { setEdit(false); if (onEditSaved) onEditSaved(); else load(); }} />;
  }
  const r = data?.row;
  const open = r && (r.state === 'open' || r.state === 'fix');
  return (
    <div className="card space-y-3 p-5 text-sm">
      <div className="flex items-center gap-2">
        <h2 className="font-bold">세금계산서 발행 요청 #{id}</h2>
        {r && <StatusChip s={r.status} />}
        <button className="btn-ghost ml-auto" onClick={onClose}>닫기</button>
      </div>
      {!r && !err && <div className="text-ink-soft">불러오는 중…</div>}
      {r && (
        <dl className="grid grid-cols-[6rem_1fr] gap-y-1 sm:grid-cols-[6rem_1fr_6rem_1fr]">
          <dt className="text-ink-soft">청구법인</dt><dd>{r.corp_name}</dd>
          <dt className="text-ink-soft">접수번호</dt><dd>{r.recv_id ?? '—'}{r.drive_mon ? ` (${r.drive_mon})` : ''}</dd>
          <dt className="text-ink-soft">구분</dt><dd>{r.recv_kind ?? '—'}</dd>
          <dt className="text-ink-soft">청구방법</dt><dd>{r.recv_method ?? '—'}</dd>
          <dt className="text-ink-soft">발행방식</dt><dd className={r.issue_way && r.issue_way !== '정발행' ? 'font-semibold text-violet-700' : ''}>{r.issue_way ?? '—'}</dd>
          <dt className="text-ink-soft">거래처</dt><dd className="font-semibold">{r.cust_name}</dd>
          <dt className="text-ink-soft">사업자번호</dt><dd>{r.biz_number ?? '—'}</dd>
          <dt className="text-ink-soft">이메일</dt><dd className="break-all">{r.email ?? '—'}</dd>
          <dt className="text-ink-soft">발행날짜</dt><dd>{r.written_date}</dd>
          <dt className="text-ink-soft">금액</dt>
          <dd>{money(r.supply)} + {money(r.vat)} = <b>{money(r.total)}</b>원 <span className="text-xs text-ink-faint">({r.vat_included ? '부가세 포함으로 요청' : '부가세 별도'})</span></dd>
          <dt className="text-ink-soft">요청</dt><dd>{r.requester_name} · {r.created_at.slice(0, 16)}</dd>
          <dt className="text-ink-soft">발행대장</dt>
          <dd>{r.invoice_no ? <>{r.invoice_no} {r.invoice_issuer ? <span className="text-emerald-700">· {r.invoice_issuer} 발행 {r.invoice_issue_date ?? ''}</span> : <span className="text-ink-faint">· 발행 전</span>}</> : '—'}</dd>
          {r.item && <><dt className="text-ink-soft">품목</dt><dd>{r.item}</dd></>}
          {r.item_note && <><dt className="text-ink-soft">비고</dt><dd>{r.item_note}</dd></>}
          {r.remark1 && <><dt className="text-ink-soft">비고1(입금 계좌)</dt><dd>{r.remark1}</dd></>}
          {(r.cc_name || r.cc_email) && <><dt className="text-ink-soft">참조자 2</dt><dd className="break-all">{[r.cc_name, r.cc_email].filter(Boolean).join(' · ')}</dd></>}
        </dl>
      )}
      {r && data?.settleDiff && (
        <div className="rounded border border-red-300 bg-red-50 px-3 py-2 text-red-800">
          ⚠ 거래처별 정산 청구액과 다릅니다 — 정산 <b>{data.settle ? `${money(data.settle.total)}원` : '그 달 정산 줄 없음'}</b> · 요청 <b>{money(r.total)}원</b>.
          <span className="text-xs"> 정산 금액이 맞으면 영업에 「보완 요청」, 요청 금액이 맞으면 정산 청구액도 고쳐야 합니다.</span>
        </div>
      )}
      {r?.note && <div className="whitespace-pre-wrap rounded bg-amber-50 px-3 py-2">{r.note}</div>}

      {data && (
        <div className="max-h-64 space-y-1.5 overflow-y-auto rounded border border-line p-2">
          {data.msgs.length === 0 && <div className="text-xs text-ink-faint">아직 주고받은 말이 없습니다.</div>}
          {data.msgs.map((m: InvReqMsg) => (
            <div key={m.id} className={`flex ${m.side === 'sales' ? 'justify-start' : m.side === 'acct' ? 'justify-end' : 'justify-center'}`}>
              {m.side === 'system'
                ? <span className="text-xs text-ink-faint">{m.body} · {m.created_at.slice(5, 16)}</span>
                : <div className={`max-w-[80%] rounded-lg px-3 py-1.5 ${m.side === 'acct' ? 'bg-brand-50' : 'bg-canvas'}`}>
                    <div className="text-[11px] text-ink-faint">{m.author_name} · {m.created_at.slice(5, 16)}</div>
                    {fileOf.has(m.id)
                      ? <FileChip f={fileOf.get(m.id)!} canRemove={canRemove(fileOf.get(m.id)!)} onRemove={() => removeFile(fileOf.get(m.id)!)} />
                      : <div className="whitespace-pre-wrap">{m.body}{m.body.startsWith('📎 ') && <span className="ml-1 text-xs text-ink-faint">(내림)</span>}</div>}
                  </div>}
            </div>
          ))}
        </div>
      )}

      {r && r.state !== 'cancel' && (
        <div className="flex gap-2">
          <input className="field" value={msg} placeholder={data?.acct && !data.mine ? '버스인접수담당자에게 남길 말' : '회계팀에 남길 말'}
                 onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && msg.trim() && !busy) run(() => api.replyInvoiceRequest({ id, body: msg })); }} />
          <button className="btn-ghost whitespace-nowrap" disabled={busy || !msg.trim()} onClick={() => run(() => api.replyInvoiceRequest({ id, body: msg }))}>보내기</button>
          {(data?.mine || data?.acct) && (
            <PickFiles label="📎 파일" disabled={busy}
                       onPick={(fs) => run(async () => { const failed = await uploadAll(id, fs); if (failed.length) throw new Error(`파일을 올리지 못했습니다: ${failed.join(' · ')}`); }, false)} />
          )}
          {data?.acct && open && (
            <button className="btn-ghost whitespace-nowrap text-orange-700" disabled={busy || !msg.trim()} title="버스인접수담당자에게 팝업으로 알립니다"
                    onClick={() => run(() => api.replyInvoiceRequest({ id, body: msg, fix: true }))}>보완 요청</button>
          )}
        </div>
      )}

      {/* 접수 없이 올라온 요청 — 사유를 보이고, 회계가 나중에 접수를 잇는다(끝난 요청도). 발행대장 계산서 접수가 비어 있으면 거기도 들어간다. */}
      {r && !r.recv_id && (
        <div className="space-y-2 rounded border border-orange-200 bg-orange-50 p-3 text-xs">
          <div className="font-semibold text-orange-800">접수 없음{r.no_recv_reason ? ` — ${r.no_recv_reason}` : ''}</div>
          {data?.acct && r.state !== 'cancel' && (
            <div className="flex flex-wrap items-center gap-2">
              <input className="field w-32" value={linkRecv} inputMode="numeric" placeholder="접수번호" onChange={(e) => setLinkRecv(e.target.value.replace(/\D/g, ''))} />
              <input type="month" className="field w-40" value={linkMon} onChange={(e) => setLinkMon(e.target.value)} title="정산월(비우면 요청의 정산월)" />
              <button className="btn-ghost" disabled={busy || !linkRecv}
                      onClick={() => run(() => api.setInvoiceRequestRecv({ id, recvId: Number(linkRecv), driveMon: linkMon || null }), false)}>접수 연결</button>
            </div>
          )}
        </div>
      )}

      {/* 회계 처리 */}
      {r && data?.acct && open && (
        <div className="space-y-2 rounded border border-brand-100 bg-brand-50/40 p-3">
          <div className="text-xs font-semibold text-brand-700">회계 처리</div>
          {!r.invoice_id ? (
            <div className="flex flex-wrap items-center gap-2">
              <input className="field w-24" value={series} placeholder="계열(비움)" title="담당 전용 계열(예: 윤일향)일 때만" onChange={(e) => setSeries(e.target.value)} />
              <button className="btn-primary" disabled={busy} onClick={() => {
                        if (data?.settleDiff && !window.confirm(`거래처별 정산 청구액(${data.settle ? `${money(data.settle.total)}원` : '그 달 정산 줄 없음'})과 요청 금액(${money(r.total)}원)이 다릅니다. 그래도 요청 금액으로 발행대장에 올릴까요?`)) return;
                        run(() => api.issueFromInvoiceRequest({ id, series: series || undefined }), false);
                      }}>발행대장에 올리기(번호)</button>
              <span className="text-ink-faint">또는</span>
              <input className="field w-40" value={linkNo} placeholder="기존 발행번호" onChange={(e) => setLinkNo(e.target.value)} />
              <button className="btn-ghost" disabled={busy || !linkNo.trim()} onClick={() => run(() => api.linkInvoiceRequest({ id, invoiceNo: linkNo.trim() }), false)}>기존 계산서 연결</button>
            </div>
          ) : null}
          <div className="flex flex-wrap items-center gap-2">
            <button className="btn-primary bg-emerald-600 hover:bg-emerald-700" disabled={busy || (!r.invoice_id && !msg.trim())}
                    title={r.invoice_id ? '발행자·발행일자를 채우고 버스인접수담당자에게 완료를 알립니다' : '발행대장 연결이 없으면 메시지(어디서 발행했는지)를 적어야 합니다'}
                    onClick={() => run(() => api.completeInvoiceRequest({ id, message: msg.trim() || undefined }))}>✅ 발행 완료</button>
            <span className="text-xs text-ink-soft">
              {r.invoice_id ? '스마트빌 발행 뒤 누르세요. 세금계산서 탭에서 발행자를 넣어도 자동으로 완료됩니다.' : '스마트빌에서 따로 발행했다면 위 칸에 발행번호를 적고 누르세요.'}
            </span>
          </div>
        </div>
      )}

      {/* #420 회계가 번호만 매기고 발행 전이면 고칠 수 있다 — 저장하면 그 번호는 내려가고 회계가 다시 올린다(서버가 발행·스마트빌·입금 연결을 막는다). */}
      {r && data?.mine && open && !r.invoice_issuer && (
        <div className="flex justify-end gap-2">
          <button className="btn-ghost" disabled={busy} onClick={() => {
            if (r.invoice_id && !window.confirm(`회계가 발행대장에 올린 요청입니다(${r.invoice_no ?? ''}). 고쳐서 저장하면 그 번호는 내려가고 회계가 고친 내용으로 다시 올립니다. 고칠까요?`)) return;
            setEdit(true);
          }}>요청 고치기</button>
          {!r.invoice_id && <button className="btn-ghost text-red-600" disabled={busy} onClick={() => run(() => api.cancelInvoiceRequest({ id, reason: msg.trim() || undefined }))}>요청 취소</button>}
        </div>
      )}
      {r && data?.acct && !data.mine && open && !r.invoice_issuer && (
        <div className="flex justify-end">
          <button className="btn-ghost text-xs text-red-600" disabled={busy} title="중복 요청 정리용 — 위 칸에 사유를 적으면 함께 남습니다"
                  onClick={() => run(() => api.cancelInvoiceRequest({ id, reason: msg.trim() || undefined }))}>요청 취소(중복 등)</button>
        </div>
      )}
      {err && <div className="rounded bg-red-50 px-3 py-2 text-red-700">{err}</div>}
    </div>
  );
}

// ─── 목록(회계 탭 · 대시보드 공용) ────────────────────────────────────────────────
function RequestList({ rows, onOpen, acct }: { rows: InvReqRow[]; onOpen: (id: number) => void; acct: boolean }) {
  // 머리글 정렬(#37) — 요청은 전부 받아 두니 화면이 정렬한다. 🔴 훅은 아래 「없음」 return 보다 위에.
  const [sort, onSort] = useSort(acct ? 'acct.invreq' : 'dash.invreq');
  const sorted = useSortedRows(rows, sort);
  if (rows.length === 0) return <div className="px-3 py-6 text-center text-sm text-ink-faint">요청이 없습니다.</div>;
  const h = (k: string | undefined, label: string, cls = 'th') => <SortTh sortKey={k} sort={sort} onSort={onSort} className={cls}>{label}</SortTh>;
  return (
    <table className="w-full text-sm">
      <thead><tr>
        {h('id', '#')}{h('status', '상태')}{h('corp_name', '청구법인')}{h('recv_id', '접수')}{h('cust_name', '거래처')}
        {h('written_date', '발행날짜')}{h('total', '합계', 'th text-right')}{acct && h('requester_name', '요청자')}{h('invoice_no', '발행번호')}{h('updated_at', '최근')}
      </tr></thead>
      <tbody>
        {sorted.map((r) => {
          const unread = acct ? r.unread_for_acct : r.unread_for_sales;
          const late = r.status === 'new' && r.age_min >= 30;
          return (
            <tr key={r.id} className={`cursor-pointer hover:bg-canvas ${late ? 'bg-red-50' : ''}`} onDoubleClick={() => onOpen(r.id)}>
              <td className="td">{r.id}</td>
              <td className="td"><StatusChip s={r.status} />{unread && <span className="ml-1 inline-block h-2 w-2 rounded-full bg-red-500" title="새 소식" />}</td>
              <td className="td whitespace-nowrap">{r.corp_name}</td>
              <td className="td">{r.recv_id ?? <span className="rounded bg-orange-100 px-1 text-xs text-orange-800" title={r.no_recv_reason ?? ''}>접수 없음</span>}{r.recv_kind ? ` ${r.recv_kind}` : ''}</td>
              <td className="td font-medium">{r.cust_name}</td>
              <td className="td whitespace-nowrap">{r.written_date}</td>
              <td className="td num">{money(r.total)}</td>
              {acct && <td className="td whitespace-nowrap">{r.requester_name}</td>}
              <td className="td whitespace-nowrap">{r.invoice_no ?? '—'}</td>
              <td className="td max-w-[18rem] truncate text-ink-soft" title={r.last_msg ?? ''}>
                {r.state === 'open' || r.state === 'fix' ? `${ago(r.age_min)} · ` : ''}{r.last_msg ?? ''}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/**
 * 회계 → 「발행 요청」 탭. `?req=번호` 로 들어오면 그 요청을 바로 연다(팝업·챗 링크).
 * 위 = 계산서가 아직 없는 요청만 · 아래 = 세금계산서 화면 통째(#161 — 두 탭을 오가지 않고 스마트빌 대량 업로드·청구완료까지 한 화면에서)
 *   + 상태 뒤에 「요청」(번호·요청 상태 → 누르면 요청 창)·「최근」 칸(#161 후속 — 위아래를 한 표로).
 * 아래는 「세금계산서」 탭과 같은 컴포넌트지만 처리할 것만 — 진행 중 요청에 이어진 회사 계산서 + 발행 전 기사 계산서(#161 후속 2) ·
 *   연도·정산월·상태 조건은 안 건다(청구완료되면 빠진다). 위 칩이 아래 표에도 걸린다(#161 후속 3): 끝난 것 = 최근 3개월 청구완료 · 전체 = 둘 다.
 */
export function InvoiceRequestTab({ corps }: { corps: AcctCorp[] }) {
  const [params, setParams] = useSearchParams();
  // 구분 칩(10-19 일원화 — 「발행 요청에서 발행 현황도 보고 취소 현황도 보고 수정 현황도」): 발행 · 취소 · 수정.
  const [kind, setKind] = useSticky<'issue' | '취소' | '수정'>('acct.invreq.kind', 'issue');
  const [cancelOpen, setCancelOpen] = useState<number | null>(null);
  const [cancelTick, setCancelTick] = useState(0);
  const [state, setState] = useSticky<'active' | 'done' | 'all' | 'norecv'>('acct.invreq.state', 'active');
  const [q, setQ] = useSticky('acct.invreq.q', '');
  const [rows, setRows] = useState<InvReqRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const pulse = useInvReqPulse();
  const [invTick, setInvTick] = useState(0); // 아래 계산서 표 다시 읽기(#308)

  // 아래 표(계산서)에 요청 상태·최근 소식을 붙이려고 요청 전부를 따로 받는다(위 목록의 상태·검색 조건과 무관).
  const [allReq, setAllReq] = useState<InvReqRow[]>([]);
  const load = useCallback(() => {
    api.listInvoiceRequests({ scope: 'all', state, q: q.trim() || undefined }).then((r) => setRows(r.rows)).catch((e) => setErr(errText(e)));
    api.listInvoiceRequests({ scope: 'all', state: 'all' }).then((r) => setAllReq(r.rows)).catch((e) => setErr(errText(e)));
  }, [state, q]);
  // #338 — 법인 고르기는 위 요청 목록·아래 계산서 표·취소/수정 목록이 한 값을 쓴다(각 법인 담당이 자기 법인 건만).
  const [corpId, setCorpId] = useSticky<number | null>('acct.invreq.corp', null);
  // 위 목록 = 아직 계산서가 없는 요청만(#161 후속 — 계산서가 생긴 요청은 아래 표 한 줄로 본다).
  const pending = useMemo(() => rows?.filter((r) => !r.invoice_id && (corpId === null || Number(r.corp_id) === corpId)) ?? null, [rows, corpId]);
  const corpSelect = (
    <select className="field w-36" value={corpId ?? ''} onChange={(e) => setCorpId(e.target.value ? Number(e.target.value) : null)} title="청구법인">
      <option value="">전체 법인</option>
      {corps.filter((c) => c.invoice_code).map((c) => <option key={c.corp_id} value={c.corp_id}>{c.short_name}({c.invoice_code})</option>)}
    </select>
  );
  const extra = useMemo<InvoiceExtraCols>(() => {
    const byInv = new Map(allReq.filter((r) => r.invoice_id).map((r) => [String(r.invoice_id), r]));
    return {
      head: ['요청', '최근'],
      cells: (inv) => {
        const r = byInv.get(String(inv.invoice_id));
        return (<>
          <td className="whitespace-nowrap px-2" onClick={(e) => e.stopPropagation()}>
            {r && <button type="button" className="inline-flex items-center gap-1 hover:underline" title="요청 창 열기" onClick={() => setOpenId(r.id)}>
              <span className="text-ink-soft">#{r.id}</span><StatusChip s={r.status} />
              {r.unread_for_acct && <span className="inline-block h-2 w-2 rounded-full bg-red-500" title="새 소식" />}
            </button>}
          </td>
          <td className="max-w-[14rem] truncate px-2 text-ink-soft" title={r?.last_msg ?? ''}>
            {r ? `${r.state === 'open' || r.state === 'fix' ? `${ago(r.age_min)} · ` : ''}${r.last_msg ?? ''}` : ''}
          </td>
        </>);
      },
    };
  }, [allReq]);
  useEffect(load, [load]);
  // 맥박이 바뀌면(새 요청·답 옴) 목록도 새로.
  const sig = pulse?.acct ? `${pulse.acct.fresh.map((f) => f.id).join(',')}|${pulse.acct.replied.map((f) => f.id).join(',')}|${pulse.acct.active}` : '';
  useEffect(() => { if (sig) { load(); setInvTick((n) => n + 1); } }, [sig]); // eslint-disable-line react-hooks/exhaustive-deps

  const reqParam = Number(params.get('req'));
  useEffect(() => {
    if (Number.isInteger(reqParam) && reqParam > 0) setOpenId(reqParam);
  }, [reqParam]);
  // `?mcr=번호` = 취소·수정 요청(팝업·구글챗 DM 버튼) — 그 건 창을 연다.
  const mcrParam = Number(params.get('mcr'));
  useEffect(() => {
    if (Number.isInteger(mcrParam) && mcrParam > 0) setCancelOpen(mcrParam);
  }, [mcrParam]);
  const closeCancel = () => {
    setCancelOpen(null);
    if (params.get('mcr')) { params.delete('mcr'); params.set('tab', 'invreq'); setParams(params, { replace: true }); }
    setCancelTick((n) => n + 1);
  };
  const cancels = pulse?.acct?.cancels ?? [];
  const nCancel = cancels.filter((c) => c.kind === '취소').length;
  const nFix = cancels.filter((c) => c.kind === '수정').length;
  const close = () => {
    setOpenId(null);
    // 🔴 #263 — `req` 만 지우면 주소가 `/accounting`(세부 탭 없음)이 되어 화면 탭이 `?req=` 로 기억된 이 탭으로
    // 되돌아가며 다시 마운트 → 창이 또 열렸다. 발행 요청 탭 주소로 바꿔 같은 탭에 머문다.
    if (params.get('req')) { params.delete('req'); params.set('tab', 'invreq'); setParams(params, { replace: true }); }
    load();
    setInvTick((n) => n + 1); // #308 번호를 매기면(발행대장에 올리기) 위 목록에서 빠지고 아래 표에 바로 나온다
  };

  const kindBar = (
    <div className="flex gap-0.5 rounded-lg bg-canvas p-0.5 text-sm">
      {([['issue', '발행', 0], ['취소', '취소', nCancel], ['수정', '수정', nFix]] as const).map(([k, label, n]) => (
        <button key={k} type="button" onClick={() => setKind(k)}
                title={k === 'issue' ? '세금계산서 발행 요청' : `영업이 올린 청구 ${label} 요청 — 승인하면 계산서 수정발행·청구상태 초기화까지 한 번에`}
                className={`whitespace-nowrap rounded-md px-2.5 py-1 font-medium ${kind === k ? 'bg-surface text-brand-700 shadow-sm' : n > 0 ? 'text-red-700' : 'text-ink-soft'}`}>
          {label}{n > 0 && <span className="ml-1 rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white">{n}</span>}
        </button>
      ))}
    </div>
  );
  const chipIds: ('active' | 'done' | 'all' | 'norecv')[] = kind === 'issue' ? ['active', 'done', 'all', 'norecv'] : ['active', 'done', 'all'];
  const stateChips = chipIds.map((s) => {
    const on = state === s || (s === 'active' && kind !== 'issue' && state === 'norecv');
    return (
      <button key={s} onClick={() => setState(s)}
              className={`rounded-lg px-2.5 py-1 text-sm ${on ? 'bg-brand-50 font-semibold text-brand-700' : 'text-ink-soft hover:bg-canvas'}`}>
        {s === 'active' ? (kind === 'issue' ? `진행 중${pulse?.acct ? ` ${pulse.acct.active}` : ''}` : '처리 대기') : s === 'done' ? '끝난 것' : s === 'norecv' ? '접수 없음' : '전체'}
      </button>
    );
  });
  const cancelModal = cancelOpen ? (
    <Modal width="max-w-2xl">
      <CancelRequestDetail id={cancelOpen} onClose={closeCancel} onDone={() => { setCancelTick((n) => n + 1); load(); setInvTick((n) => n + 1); }} />
    </Modal>
  ) : null;

  if (kind !== 'issue') {
    return (
      <div className="space-y-2">
        <CancelRequestPanel kind={kind} state={state === 'norecv' ? 'active' : state} q={q} corps={corps} reloadKey={cancelTick}
          onOpen={(id) => setCancelOpen(id)}
          toolbar={<>
            {kindBar}
            {stateChips}
            <input className="field w-52" value={q} placeholder="거래처·접수번호·요청자" onChange={(e) => setQ(e.target.value)} />
          </>} />
        {cancelModal}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="btn-row-sm flex flex-wrap items-center gap-2">
        {kindBar}
        {stateChips}
        {corpSelect}
        <input className="field w-60" value={q} placeholder="거래처·접수번호·요청자·발행번호" onChange={(e) => setQ(e.target.value)} />
        <span className="page-hint min-w-0 flex-1">새 요청은 어느 화면에 있든 팝업으로 알려 드립니다. {pulse?.acct?.overdueMin ?? 30}분 넘게 아무도 안 연 요청은 빨갛게 표시됩니다.</span>
        <button className="btn-ghost ml-auto" onClick={() => setNewOpen(true)}>+ 대신 요청 올리기</button>
      </div>
      {cancels.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-sm text-red-800">
          <b>처리 대기 취소·수정 요청 {cancels.length}건</b>
          {cancels.slice(0, 4).map((c) => (
            <button key={c.id} type="button" className="inline-flex items-center gap-1 rounded border border-red-200 bg-white px-1.5 py-0.5 text-xs hover:bg-red-100"
                    onClick={() => setCancelOpen(c.id)}>
              <KindChip kind={c.kind} />{c.cust_name ?? `접수 ${c.recv_id}`} {c.drive_mon}
            </button>
          ))}
          <button type="button" className="ml-auto text-xs underline" onClick={() => setKind(nCancel > 0 ? '취소' : '수정')}>전부 보기</button>
        </div>
      )}
      {err && <div className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}
      {/* 위 = 계산서가 아직 없는 요청만(발행대장에 올리기 전) · 높이를 묶어 아래 표가 같이 보이게. */}
      {pending === null ? <div className="card p-4 text-sm text-ink-soft">불러오는 중…</div>
        : pending.length === 0 ? <p className="text-xs text-ink-faint">발행대장에 올리기 전 요청이 없습니다. 계산서가 생긴 요청은 아래 표에서 요청 상태와 최근 소식을 함께 봅니다.</p>
        : <div className="card max-h-[30vh] overflow-auto">
            <div className="px-3 pt-2 text-xs font-semibold text-ink-soft">발행대장에 올리기 전 요청 {pending.length}건</div>
            <RequestList rows={pending} onOpen={setOpenId} acct />
          </div>}
      <div className="border-t border-line pt-3">
        <InvoiceTab corps={corps} requestOnly requestScope={state === 'done' ? 'done' : state === 'all' ? 'all' : 'active'} extra={extra} reloadKey={invTick}
                    corpCtl={[corpId, setCorpId] as const} />
      </div>
      {openId && <Modal>
        <InvoiceRequestDetail id={openId} onClose={close} />
      </Modal>}
      {newOpen && <Modal>
        <InvoiceRequestForm onClose={() => setNewOpen(false)} onSaved={(id) => { setNewOpen(false); setOpenId(id); }} />
      </Modal>}
      {cancelModal}
    </div>
  );
}

/** 대시보드 — 내가 올린 발행 요청(진행 중 + 최근 끝난 것). 누구나. */
export function MyInvoiceRequests() {
  const [rows, setRows] = useState<InvReqRow[] | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const pulse = useInvReqPulse();
  const load = useCallback(() => {
    api.listInvoiceRequests({ scope: 'mine', state: 'all' }).then((r) => setRows(r.rows.slice(0, 15))).catch(() => setRows([]));
  }, []);
  useEffect(load, [load]);
  const sig = pulse ? `${pulse.mine.map((m) => `${m.id}${m.status}`).join(',')}|${pulse.myActive}` : '';
  useEffect(() => { if (sig) load(); }, [sig]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="card p-4">
      <div className="flex items-center gap-2">
        <div className="font-semibold">내 세금계산서 발행 요청</div>
        <span className="text-xs text-ink-soft">회계팀이 처리하면 팝업으로 알려 드립니다.</span>
        <button className="btn-primary ml-auto" onClick={() => setNewOpen(true)}>+ 발행 요청</button>
      </div>
      {rows && rows.length > 0 && <div className="mt-2 overflow-x-auto"><RequestList rows={rows} onOpen={setOpenId} acct={false} /></div>}
      {openId && <Modal>
        <InvoiceRequestDetail id={openId} onClose={() => { setOpenId(null); load(); }} />
      </Modal>}
      {newOpen && <Modal>
        <InvoiceRequestForm onClose={() => setNewOpen(false)} onSaved={(id) => { setNewOpen(false); setOpenId(id); load(); }} />
      </Modal>}
    </div>
  );
}

// ─── 환기 — 어느 화면이든(Shell 에 하나) ─────────────────────────────────────────
const POLL_MS = 45_000;
const SEEN_KEY = 'invreq-alerted-v1';
/** 회계 취소·수정 팝업 「나중에」 — 이 창(세션)에서만 10분. */
const SNOOZE_KEY = 'misucancel-snooze-v1';
const ACCT_SNOOZE_KEY = 'invreq-acct-snooze-v1';
const SNOOZE_MS = 10 * 60_000;
// #416(정여진) — 회계 발행 요청 팝업 「확인」(이 창 동안) · 「다시 보지 않기」(브라우저를 다시 열어도) =
// 지금 떠 있는 요청·답만 접는다. 새 요청이나 그 요청에 새 답이 오면 다시 뜬다(키에 답 시각).
const ACCT_OK_KEY = 'invreq-acct-ok-v1';
const ACCT_MUTE_KEY = 'invreq-acct-mute-v1';
type AcctItems = { fresh: { id: number }[]; replied: { id: number; changed_at?: string | null }[] };
const acctKeys = (p: AcctItems) => [...p.fresh.map((f) => `f${f.id}`), ...p.replied.map((r) => `r${r.id}:${r.changed_at ?? ''}`)];
function loadKeys(store: Storage, key: string): Set<string> {
  try { return new Set(JSON.parse(store.getItem(key) ?? '[]') as string[]); } catch { return new Set(); }
}
function saveKeys(store: Storage, key: string, s: Set<string>) {
  try { store.setItem(key, JSON.stringify([...s].slice(-300))); } catch { /* 못 남기면 다시 뜰 뿐 */ }
}
function loadSeen(): Set<string> {
  try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) ?? '[]') as string[]); } catch { return new Set(); }
}
function saveSeen(s: Set<string>) {
  try { localStorage.setItem(SEEN_KEY, JSON.stringify([...s].slice(-300))); } catch { /* 저장 못 해도 팝업이 한 번 더 뜰 뿐 */ }
}
function chime() {
  try {
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    [880, 1175].forEach((f, i) => {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.frequency.value = f; o.connect(g); g.connect(ctx.destination);
      const t = ctx.currentTime + i * 0.18;
      g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(0.15, t + 0.02); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.3);
      o.start(t); o.stop(t + 0.32);
    });
    setTimeout(() => void ctx.close(), 1000);
  } catch { /* 소리는 없어도 그만 — 브라우저가 첫 클릭 전 소리를 막는다 */ }
}
function desktop(title: string, body: string, url: string) {
  try {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    const n = new Notification(title, { body, tag: url, icon: '/favicon.svg' });
    n.onclick = () => { window.focus(); window.location.assign(url); n.close(); };
  } catch { /* 알림 막힘 */ }
}

export function InvoiceRequestAlerts() {
  const navigate = useNavigate();
  const [pulse, setPulse] = useState<InvReqPulse | null>(null);
  const [acctPop, setAcctPop] = useState<AcctPop | null>(null);
  const [salesPop, setSalesPop] = useState<InvReqPulse['mine'] | null>(null);
  const [detail, setDetail] = useState<number | null>(null);
  // 청구 취소·수정(10-19 일원화) — 회계 = 대기 요청 전부(처리될 때까지 · 「나중에」 10분) · 영업 = 내 결과(「확인」까지).
  const [cancelPop, setCancelPop] = useState<MisuCancelPulse[] | null>(null);
  const [cancelMine, setCancelMine] = useState<MisuCancelPulse[] | null>(null);
  const [reRequest, setReRequest] = useState<{ recvId: number; driveMon: string } | null>(null);
  const snoozeUntil = useRef<number>(Number(sessionStorage.getItem(SNOOZE_KEY) ?? 0) || 0);
  const acctSnooze = useRef<number>(Number(sessionStorage.getItem(ACCT_SNOOZE_KEY) ?? 0) || 0);
  // 요청자 DM 링크(`/?myreq=번호`) — 어느 화면에서든 그 요청 창을 바로 연다.
  const location = useLocation();
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const id = Number(p.get('myreq'));
    if (!Number.isInteger(id) || id <= 0) return;
    setDetail(id);
    p.delete('myreq');
    navigate({ pathname: location.pathname, search: p.toString() ? `?${p}` : '' }, { replace: true });
  }, [location.search, location.pathname, navigate]);
  const [perm, setPerm] = useState(typeof Notification === 'undefined' ? 'unsupported' : Notification.permission);
  const seen = useRef<Set<string>>(loadSeen());
  const baseTitle = useRef(document.title);

  const tick = useCallback(() => {
    invoiceRequestPulse().then((p) => {
      setPulse(p); publish(p);
      // 회계 — 처음 보는 새 요청·답 온 것만 팝업(같은 것을 45초마다 다시 띄우지 않는다. 배지·빨간 띠는 계속 남는다).
      // 🔴 10-19 way — 판정은 서버가 사람마다(내가 그 요청을 열 때까지 남는다). 「나중에」 = 10분 쉼, 소리·데스크톱 알림은 처음 보는 것만.
      if (p.acct && p.acct.handler !== false && p.acct.fresh.length + p.acct.replied.length > 0) {
        // 「확인」·「다시 보지 않기」로 접은 것만 남았으면 띄우지 않는다(하나라도 새 것이면 전부 보여 준다).
        const folded = new Set([...loadKeys(sessionStorage, ACCT_OK_KEY), ...loadKeys(localStorage, ACCT_MUTE_KEY)]);
        const allFolded = acctKeys(p.acct).every((k) => folded.has(k));
        if (Date.now() >= acctSnooze.current && !allFolded) setAcctPop({ fresh: p.acct.fresh, replied: p.acct.replied });
        else if (allFolded) setAcctPop(null);
        const fresh = p.acct.fresh.filter((f) => !seen.current.has(`f${f.id}`));
        const replied = p.acct.replied.filter((f) => !seen.current.has(`r${f.id}`));
        if (fresh.length + replied.length > 0) {
          fresh.forEach((f) => seen.current.add(`f${f.id}`));
          replied.forEach((f) => seen.current.add(`r${f.id}`));
          saveSeen(seen.current);
          acctSnooze.current = 0;
          if (!allFolded) setAcctPop({ fresh: p.acct.fresh, replied: p.acct.replied });
          chime();
          const first = fresh[0] ?? null;
          desktop(first ? `🧾 세금계산서 발행 요청 — ${first.cust_name}` : '💬 발행 요청에 영업 답변',
            first ? `${first.corp_name ?? ''} · ${money(first.total)}원 · ${first.requester_name ?? ''}` : replied.map((r) => r.cust_name).join(', '),
            `/accounting?req=${first?.id ?? replied[0]!.id}`);
        }
      } else setAcctPop(null);
      // 회계 — 대기 중인 취소·수정 요청은 처리될 때까지 다시 뜬다(「나중에」를 누르면 10분 쉼). 소리·데스크톱 알림은 처음 보는 건만.
      const cancels = p.acct && p.acct.handler !== false ? p.acct.cancels ?? [] : [];
      if (cancels.length > 0) {
        const fresh = cancels.filter((c) => !seen.current.has(`c${c.id}`));
        if (fresh.length) {
          fresh.forEach((c) => seen.current.add(`c${c.id}`));
          saveSeen(seen.current);
          chime();
          snoozeUntil.current = 0; // 새 요청이 오면 쉬던 것도 깬다
          const c = fresh[0]!;
          desktop(`🧾 청구 ${c.kind} 요청 — ${c.cust_name ?? `접수 ${c.recv_id}`}`, `${c.requested_name ?? ''} · ${c.reason}`, `/accounting?tab=invreq&mcr=${c.id}`);
        }
        setCancelPop(Date.now() >= snoozeUntil.current ? cancels : null);
      } else setCancelPop(null);
      // 영업 — 내 취소·수정 요청 결과.
      const results = p.cancelMine ?? [];
      if (results.length > 0) {
        const fresh = results.filter((c) => !seen.current.has(`cm${c.id}:${c.status}`));
        if (fresh.length) {
          fresh.forEach((c) => seen.current.add(`cm${c.id}:${c.status}`));
          saveSeen(seen.current);
          chime();
          const c = fresh[0]!;
          desktop(c.status === '승인' ? (c.kind === '취소' ? `↩ 청구 취소 완료 — ${c.cust_name ?? ''}` : `✏️ 청구 수정 승인 — ${c.cust_name ?? ''}`) : `❗ 청구 ${c.kind} 반려 — ${c.cust_name ?? ''}`,
            c.decide_note ?? '', '/');
        }
        setCancelMine(results);
      } else setCancelMine(null);
      // 영업 — 회계 쪽 소식이 있으면 「확인」 누를 때까지 팝업. 같은 소식은 한 번만 소리·데스크톱 알림.
      if (p.mine.length > 0) {
        const keyOf = (m: InvReqPulse['mine'][number]) => `m${m.id}:${m.status}:${m.last_msg ?? ''}`;
        const newer = p.mine.filter((m) => !seen.current.has(keyOf(m)));
        if (newer.length > 0) {
          newer.forEach((m) => seen.current.add(keyOf(m)));
          saveSeen(seen.current);
          chime();
          const m = newer[0]!;
          desktop(m.status === 'done' ? `✅ 세금계산서 발행 완료 — ${m.cust_name}` : m.status === 'fix' ? `❗ 보완 요청 — ${m.cust_name}` : `💬 회계팀 답변 — ${m.cust_name}`,
            m.last_msg ?? '', '/');
        }
        setSalesPop(p.mine);
      } else setSalesPop(null);
    }).catch(() => { /* 알림은 없어도 업무 화면은 그대로 */ });
  }, []);

  useEffect(() => {
    tick();
    const t = setInterval(() => { if (document.visibilityState === 'visible') tick(); }, POLL_MS);
    const onVis = () => { if (document.visibilityState === 'visible') tick(); };
    window.addEventListener('focus', tick);
    window.addEventListener('invreq:poke', tick);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(t);
      window.removeEventListener('focus', tick);
      window.removeEventListener('invreq:poke', tick);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [tick]);

  // 탭 제목에 숫자 — 다른 탭에 있어도 보인다.
  const handler = !!pulse?.acct && pulse.acct.handler !== false;
  const count = (handler && pulse?.acct ? pulse.acct.fresh.length + pulse.acct.replied.length + (pulse.acct.cancels?.length ?? 0) : 0)
    + (pulse?.mine.length ?? 0) + (pulse?.cancelMine?.length ?? 0);
  useEffect(() => {
    document.title = count > 0 ? `(${count}) ${baseTitle.current}` : baseTitle.current;
  }, [count]);

  const askPerm = () => {
    if (typeof Notification === 'undefined') return;
    void Notification.requestPermission().then((p) => setPerm(p));
  };
  const permLine = perm === 'default' && (
    <button className="text-xs text-brand-700 underline" onClick={askPerm}>다른 프로그램을 보고 있을 때도 알림 받기(데스크톱 알림 켜기)</button>
  );

  async function ack(ids: number[]) {
    try { await api.ackInvoiceRequests({ ids }); } catch { /* 다음 맥박에 다시 뜬다 */ }
    setSalesPop(null); tick();
  }
  async function ackCancel(ids: number[]) {
    try { await api.ackMisuCancelResults({ ids }); } catch { /* 다음 맥박에 다시 뜬다 */ }
    setCancelMine(null); tick();
  }
  /** 회계 취소·수정 팝업 쉬기(10분) — 그 건을 열어 처리하는 동안에도 다시 덮지 않게. */
  const snooze = () => {
    snoozeUntil.current = Date.now() + SNOOZE_MS;
    try { sessionStorage.setItem(SNOOZE_KEY, String(snoozeUntil.current)); } catch { /* 새로고침하면 다시 뜰 뿐 */ }
    setCancelPop(null);
  };
  /** 회계 새 요청·답 팝업 쉬기(10분) — 내가 열 때까지 다시 뜬다(10-19 way). */
  const acctLater = () => {
    acctSnooze.current = Date.now() + SNOOZE_MS;
    try { sessionStorage.setItem(ACCT_SNOOZE_KEY, String(acctSnooze.current)); } catch { /* 새로고침하면 다시 뜰 뿐 */ }
    setAcctPop(null);
  };
  /** #416 — 지금 떠 있는 요청·답 접기. mute = 브라우저를 다시 열어도(새 요청·새 답이 오면 다시 뜬다). */
  const acctFold = (mute: boolean) => {
    if (!acctPop) return;
    const store = mute ? localStorage : sessionStorage;
    const key = mute ? ACCT_MUTE_KEY : ACCT_OK_KEY;
    const s = loadKeys(store, key);
    acctKeys(acctPop).forEach((k) => s.add(k));
    saveKeys(store, key, s);
    setAcctPop(null);
  };

  return (
    <>
      {handler && pulse?.acct && pulse.acct.overdue > 0 && (
        <button onClick={() => navigate('/accounting?tab=invreq')}
                className="mb-2 block w-full rounded-lg bg-red-600 px-4 py-2 text-left text-sm font-semibold text-white hover:bg-red-700">
          ⏰ {pulse.acct.overdueMin}분 넘게 아무도 확인하지 않은 세금계산서 발행 요청이 {pulse.acct.overdue}건 있습니다 — 눌러서 처리하기
        </button>
      )}

      {acctPop && (
        <Modal width="max-w-lg">
          <AcctAlertCard pop={acctPop} permLine={permLine} go={(id) => { acctLater(); navigate(id ? `/accounting?req=${id}` : '/accounting?tab=invreq'); }} onLater={acctLater}
                         onOk={() => acctFold(false)} onMute={() => acctFold(true)} />
        </Modal>
      )}

      {cancelPop && !acctPop && (
        <Modal width="max-w-lg">
          <CancelAlertCard pop={cancelPop} permLine={permLine}
            go={(id) => { snooze(); navigate(`/accounting?tab=invreq&mcr=${id}`); }} onLater={snooze} />
        </Modal>
      )}

      {salesPop && !acctPop && !cancelPop && !detail && (
        <Modal width="max-w-lg">
          <SalesAlertCard pop={salesPop} permLine={permLine} onOpen={setDetail} onAck={() => ack(salesPop.map((m) => m.id))} />
        </Modal>
      )}
      {cancelMine && !salesPop && !acctPop && !cancelPop && !detail && !reRequest && (
        <Modal width="max-w-lg">
          <CancelResultCard pop={cancelMine} permLine={permLine} onAck={() => ackCancel(cancelMine.map((m) => m.id))}
            onReRequest={(c) => { void ackCancel([c.id]); setReRequest({ recvId: c.recv_id, driveMon: c.drive_mon }); }}
            onOpenRequest={(id) => { void ackCancel(cancelMine.map((m) => m.id)); setDetail(id); }} />
        </Modal>
      )}
      {reRequest && <Modal>
        <InvoiceRequestForm prefill={reRequest} onClose={() => setReRequest(null)} onSaved={(id) => { setReRequest(null); setDetail(id); }} />
      </Modal>}
      {detail && <Modal>
        <InvoiceRequestDetail id={detail} onClose={() => { setDetail(null); tick(); }} />
      </Modal>}
    </>
  );
}

type AcctPop = { fresh: NonNullable<InvReqPulse['acct']>['fresh']; replied: NonNullable<InvReqPulse['acct']>['replied'] };
function AcctAlertCard({ pop, permLine, go, onLater, onOk, onMute }: {
  pop: AcctPop; permLine: ReactNode; go: (id: number | null) => void; onLater: () => void; onOk: () => void; onMute: () => void;
}) {
  return (
    <div className="card space-y-3 p-5 text-sm">
      <h2 className="text-base font-bold">🧾 세금계산서 발행 요청이 왔습니다</h2>
      {pop.fresh.length > 0 && (
        <ul className="divide-y rounded border border-line">
          {pop.fresh.map((f) => (
            <li key={f.id}>
              <button className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-canvas"
                      onClick={() => go(f.id)}>
                <span className="font-semibold">{f.cust_name}</span>
                <span className="text-ink-soft">{f.corp_name} · {f.requester_name}</span>
                {f.seen_name && <span className="whitespace-nowrap text-xs text-ink-faint">({f.seen_name} 님 확인)</span>}
                <span className="ml-auto whitespace-nowrap">{money(f.total)}원</span>
                <span className={`whitespace-nowrap text-xs ${f.age_min >= 30 ? 'font-bold text-red-600' : 'text-ink-faint'}`}>{ago(f.age_min)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {pop.replied.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-ink-soft">버스인접수담당자가 답했습니다</div>
          <ul className="mt-1 divide-y rounded border border-line">
            {pop.replied.map((f) => (
              <li key={f.id}>
                <button className="flex w-full gap-2 px-3 py-2 text-left hover:bg-canvas" onClick={() => go(f.id)}>
                  <span className="font-semibold">{f.cust_name}</span><span className="text-ink-soft">{f.requester_name}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {permLine}
      <div className="flex flex-wrap justify-end gap-2">
        <button className="btn-ghost mr-auto text-ink-soft" onClick={onMute}
                title="지금 뜬 요청은 다시 띄우지 않습니다. 새 요청이 오면 다시 뜹니다.">다시 보지 않기</button>
        <button className="btn-ghost" onClick={onLater} title="10분 뒤 다시 띄웁니다">나중에</button>
        <button className="btn" onClick={onOk} title="지금 뜬 요청은 이 창에서 다시 띄우지 않습니다">확인</button>
        <button className="btn-primary" onClick={() => go(pop.fresh[0]?.id ?? pop.replied[0]?.id ?? null)}>
          지금 처리하기
        </button>
      </div>
    </div>
  );
}

/** 회계 — 처리 대기 취소·수정 요청. 「취소/수정」 · 사유 · 바뀐 금액 · 처음 확인한 사람(10-19 way 「누가 먼저 확인했는지」). */
function CancelAlertCard({ pop, permLine, go, onLater }: { pop: MisuCancelPulse[]; permLine: ReactNode; go: (id: number) => void; onLater: () => void }) {
  return (
    <div className="card space-y-3 p-5 text-sm">
      <h2 className="text-base font-bold">↩ 청구 취소·수정 요청이 {pop.length}건 기다립니다</h2>
      <ul className="max-h-[50vh] divide-y overflow-y-auto rounded border border-line">
        {pop.map((c) => (
          <li key={c.id}>
            <button className="w-full px-3 py-2 text-left hover:bg-canvas" onClick={() => go(c.id)}>
              <div className="flex items-center gap-2">
                <KindChip kind={c.kind} />
                <span className="font-semibold">{c.cust_name ?? `접수 ${c.recv_id}`}</span>
                <span className="text-xs text-ink-soft">접수 {c.recv_id} · {c.drive_mon}{c.corp_name ? ` · ${c.corp_name}` : ''}</span>
                <span className="ml-auto whitespace-nowrap text-xs text-ink-faint">{c.requested_name} · {c.requested_at.slice(5, 16)}</span>
              </div>
              {c.kind === '수정' && (
                <div className="mt-1 font-semibold text-violet-800">
                  {c.old_total ? money(c.old_total) : '—'} → {money(c.fix_total)}원{c.fix_note ? ` · ${c.fix_note}` : ''}
                </div>
              )}
              <div className="mt-0.5 text-ink-soft">사유: {c.reason}</div>
              <div className={`mt-0.5 text-xs ${c.seen_name ? 'text-amber-800' : 'font-semibold text-red-600'}`}>
                {c.seen_name ? `${c.seen_name} 님이 확인 중 (${(c.seen_at ?? '').slice(11, 16)})` : '아직 아무도 확인하지 않았습니다'}
              </div>
            </button>
          </li>
        ))}
      </ul>
      {permLine}
      <div className="flex justify-end gap-2">
        <button className="btn-ghost" onClick={onLater}>나중에(10분)</button>
        <button className="btn-primary" onClick={() => go(pop[0]!.id)}>지금 처리하기</button>
      </div>
    </div>
  );
}

/** 영업 — 내 취소·수정 요청 결과. 취소 완료면 「같은 접수번호로 다시 청구 요청」. */
function CancelResultCard({ pop, permLine, onAck, onReRequest, onOpenRequest }: {
  pop: MisuCancelPulse[]; permLine: ReactNode; onAck: () => void; onReRequest: (c: MisuCancelPulse) => void; onOpenRequest: (id: number) => void;
}) {
  return (
    <div className="card space-y-3 p-5 text-sm">
      <h2 className="text-base font-bold">청구 취소·수정 요청 결과가 왔습니다</h2>
      <ul className="divide-y rounded border border-line">
        {pop.map((c) => {
          const ok = c.status === '승인';
          return (
            <li key={c.id} className="px-3 py-2">
              <div className="flex items-center gap-2">
                <KindChip kind={c.kind} />
                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${ok ? 'bg-emerald-100 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                  {ok ? (c.kind === '취소' ? '취소 완료' : '수정 승인') : '반려'}
                </span>
                <span className="font-semibold">{c.cust_name ?? `접수 ${c.recv_id}`}</span>
                <span className="text-xs text-ink-soft">접수 {c.recv_id} · {c.drive_mon}</span>
              </div>
              <div className={`mt-1 ${ok ? 'text-ink' : 'font-semibold text-red-700'}`}>
                {!ok ? `반려 사유: ${c.decide_note ?? ''}`
                  : c.kind === '취소' ? `계산서를 취소하고 청구 요청 전으로 돌렸습니다 — 금액을 고친 뒤 같은 접수번호(${c.recv_id})로 다시 청구 요청해 주세요.`
                    : `회계팀이 ${money(c.fix_total)}원으로 다시 발행합니다 — 정산 청구 금액도 바뀐 금액에 맞춰 주세요(다시 발행되기 전까지 고칠 수 있습니다).`}
              </div>
              <div className="mt-0.5 text-xs text-ink-faint">처리: {c.decided_name ?? ''} · {(c.decided_at ?? '').slice(5, 16)}{ok && c.decide_note ? ` · ${c.decide_note}` : ''}</div>
              {ok && c.kind === '취소' && (
                <button className="btn-primary mt-2 px-3 py-1 text-xs" onClick={() => onReRequest(c)}>같은 접수번호로 다시 청구 요청</button>
              )}
              {ok && c.kind === '수정' && c.reopen_request_id ? (
                <button className="btn-ghost mt-2 px-3 py-1 text-xs" onClick={() => onOpenRequest(c.reopen_request_id!)}>다시 열린 발행 요청 #{c.reopen_request_id} 보기</button>
              ) : null}
            </li>
          );
        })}
      </ul>
      {permLine}
      <div className="flex justify-end gap-2">
        <button className="btn-primary" onClick={onAck}>확인</button>
      </div>
    </div>
  );
}

function SalesAlertCard({ pop, permLine, onOpen, onAck }: { pop: InvReqPulse['mine']; permLine: ReactNode; onOpen: (id: number) => void; onAck: () => void }) {
  return (
    <div className="card space-y-3 p-5 text-sm">
      <h2 className="text-base font-bold">세금계산서 요청에 회계팀 소식이 있습니다</h2>
      <ul className="divide-y rounded border border-line">
        {pop.map((m) => (
          <li key={m.id}>
            <button className="w-full px-3 py-2 text-left hover:bg-canvas" onClick={() => onOpen(m.id)}>
              <div className="flex items-center gap-2">
                <StatusChip s={m.status} />
                <span className="font-semibold">{m.cust_name}</span>
                <span className="ml-auto whitespace-nowrap">{money(m.total)}원</span>
              </div>
              <div className={`mt-1 ${m.status === 'fix' ? 'font-semibold text-orange-800' : (m.last_msg ?? '').startsWith('↩ 발행 취소') ? 'font-semibold text-red-700' : 'text-ink-soft'}`}>
                {m.status === 'done' ? `발행되었습니다${m.invoice_no ? ` — 번호 ${m.invoice_no}` : ''}` : m.last_msg}
              </div>
            </button>
          </li>
        ))}
      </ul>
      {pop.some((m) => m.status === 'fix') && <div className="text-xs text-orange-800">보완 요청은 눌러서 답하시거나 요청을 고쳐 다시 올려 주세요.</div>}
      {permLine}
      <div className="flex justify-end gap-2">
        <button className="btn-primary" onClick={onAck}>확인</button>
      </div>
    </div>
  );
}

/** 머리글 버튼 — 어느 화면에서든 바로 요청을 올린다(챗 창을 여는 것만큼 빨라야 옮겨 온다). */
export function InvoiceRequestHeaderButton() {
  const [open, setOpen] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);
  return (
    <>
      <button className="btn-ghost whitespace-nowrap px-3 py-1" onClick={() => setOpen(true)} title="세금계산서 발행 요청 올리기">🧾 계산서 요청</button>
      {open && <Modal>
        <InvoiceRequestForm onClose={() => setOpen(false)} onSaved={(id) => { setOpen(false); setOpenId(id); }} />
      </Modal>}
      {openId && <Modal>
        <InvoiceRequestDetail id={openId} onClose={() => setOpenId(null)} />
      </Modal>}
    </>
  );
}

/** 담당자 고르기 칸의 한 줄 — 「홍길동 (정산 · 총무팀) · a@b.com」 */
function contactLabel(c: { kind: string; dept?: string; name: string; email: string }) {
  const tag = [c.kind === '정산담당자' ? '정산' : '', c.dept ?? ''].filter(Boolean).join(' · ');
  return `${c.name || '이름 없음'}${tag ? ` (${tag})` : ''} · ${c.email}`;
}
